// Repeat bookings: the choices in the new-booking panel, as the API's rule
// and until (beu-node-v2 bookingSeries/seriesRules.js). Pure, for tests.
import moment from 'moment'

export const FREQUENCIES = [
  { id: 'none', label: 'Doesn’t repeat' },
  { id: 'w1', label: 'Every week', rule: { every: 1 } },
  { id: 'w2', label: 'Every 2 weeks', rule: { every: 2 } },
  { id: 'w3', label: 'Every 3 weeks', rule: { every: 3 } },
  { id: 'w4', label: 'Every 4 weeks', rule: { every: 4 } },
  { id: 'w5', label: 'Every 5 weeks', rule: { every: 5 } },
  { id: 'w6', label: 'Every 6 weeks', rule: { every: 6 } },
  { id: 'w8', label: 'Every 8 weeks', rule: { every: 8 } },
  { id: 'w10', label: 'Every 10 weeks', rule: { every: 10 } },
  { id: 'w12', label: 'Every 12 weeks', rule: { every: 12 } },
  { id: 'm1', label: 'Every month', rule: { monthly: true } },
]

// "No end date" books the next 12 months (the server keeps a year ahead);
// a date can be as far ahead as they like, so it says so
export const ENDS = [
  { id: 'never', label: 'Next 12 months' },
  { id: 'date', label: 'Until a date, any length' },
  { id: 'count', label: 'After a number of visits' },
]

// Quick picks for the end date, from the first visit
export const END_PICKS = [
  { months: 6, label: '6 months' },
  { months: 12, label: '1 year' },
  { months: 18, label: '18 months' },
  { months: 24, label: '2 years' },
]
export const endAfter = (firstDate, months) => moment(firstDate).add(months, 'months').format('YYYY-MM-DD')

export const ruleFor = (freq) => (FREQUENCIES.find(f => f.id === freq) || {}).rule || null

export const untilFor = ({ end, endDate, endCount }) => {
  if (end === 'date' && endDate) return { date: moment(endDate).format('YYYY-MM-DD') }
  if (end === 'count' && Number(endCount) >= 2) return { count: Math.round(Number(endCount)) }
  return {}
}

// What the create and check calls get. "Next 12 months" is 12 months from
// today on the server, so a repeat starting far ahead (a year out, say)
// would only fit one visit and be refused. Starting more than 6 months out,
// the 12 months are sent as a real end date from the first visit instead.
export const untilToSend = (repeat, firstDate, now = new Date()) => {
  if (repeat && repeat.end === 'never' && firstDate && moment(firstDate).isAfter(moment(now).add(6, 'months'), 'day')) {
    return { date: moment(firstDate).add(12, 'months').subtract(1, 'day').format('YYYY-MM-DD') }
  }
  return untilFor(repeat || {})
}

const ORD = ['1st', '2nd', '3rd', '4th', 'last']
// "Every 6 weeks until 12 Mar 2027", "Every month on the 2nd Tuesday, no end date"
export const repeatSaid = ({ freq, end, endDate, endCount, firstDate }) => {
  const f = FREQUENCIES.find(x => x.id === freq)
  if (!f || !f.rule) return ''
  let what = f.label
  if (f.rule.monthly && firstDate) {
    const d = moment(firstDate)
    const n = Math.ceil(d.date() / 7)
    const last = moment(d).add(7, 'days').month() !== d.month()
    what = `Every month on the ${last && n >= 4 ? 'last' : ORD[n - 1]} ${d.format('dddd')}`
  }
  const u = untilFor({ end, endDate, endCount })
  if (u.date) return `${what} until ${moment(u.date).format('D MMM YYYY')}`
  if (u.count) return `${what}, ${u.count} visits`
  return `${what}, for the next 12 months`
}

// The end choice is complete enough to save
export const endIsValid = ({ end, endDate, endCount, firstDate }) => {
  if (end === 'date') return !!endDate && (!firstDate || moment(endDate).isAfter(moment(firstDate), 'day'))
  if (end === 'count') return Number(endCount) >= 2 && Number(endCount) <= 520
  return true
}

// "14 Mar" for the clash list
export const dateSaid = (date) => moment(date, 'YYYY-MM-DD').format('ddd D MMM')

// The cancel sheet's choices for a repeat booking
export const cancelScopeSaid = (scope) => ({
  this: 'Just this booking',
  following: 'This and all after it',
  all: 'All upcoming visits',
}[scope] || 'Just this booking')

// ─── How many visits ───
// The date of the i-th monthly visit after `start`: the same weekday in the
// same week of the month ("2nd Tuesday"), or the last one when the first
// visit was in the last week (as repeatSaid says it)
const monthlyDate = (start, i) => {
  const s = moment(start)
  const n = Math.ceil(s.date() / 7)
  const isLast = moment(s).add(7, 'days').month() !== s.month() && n >= 4
  const m = moment(s).startOf('month').add(i, 'months')
  if (isLast) {
    const d = moment(m).endOf('month').startOf('day')
    while (d.day() !== s.day()) d.subtract(1, 'day')
    return d
  }
  const d = moment(m)
  while (d.day() !== s.day()) d.add(1, 'day')
  return d.add(n - 1, 'weeks')
}

// How many visits a repeat makes, or null when it has no end date (kept a
// year ahead, so the number changes as time passes)
export const plannedVisits = ({ freq, end, endDate, endCount, firstDate }) => {
  const f = FREQUENCIES.find(x => x.id === freq)
  if (!f || !f.rule || !firstDate) return null
  if (end === 'count') {
    const n = Math.round(Number(endCount))
    return n >= 2 ? n : null
  }
  if (end !== 'date' || !endDate) return null
  const start = moment(firstDate).startOf('day')
  const last = moment(endDate).startOf('day')
  if (!last.isAfter(start)) return null
  if (f.rule.every) return Math.floor(last.diff(start, 'days') / (7 * f.rule.every)) + 1
  let n = 0
  for (let i = 0; i < 1200; i += 1) {
    if (monthlyDate(start, i).isAfter(last)) break
    n += 1
  }
  return n
}

// The save button: "Book 9 visits", or "Book 12 months" with no end date
export const bookLabel = (repeat, firstDate) => {
  const n = plannedVisits({ ...repeat, firstDate })
  return n ? `Book ${n} visits` : 'Book 12 months'
}

// ─── Check the dates ───
// What the check found, for the summary at the top
export const checkSummary = (dates) => {
  const list = dates || []
  return {
    ok: list.filter(d => d.status === 'ok').length,
    overlap: list.filter(d => d.status === 'overlap').length,
    blocked: list.filter(d => d.status === 'blocked').length,
  }
}

// The screen opens whenever a date overlaps another booking or can't be
// booked; when every date is free the repeat books straight away
export const needsCheck = (res) => {
  if (!res) return false
  const s = checkSummary(res.dates)
  return Number(res.blocked) > 0 || Number(res.overlapping) > 0 || s.blocked > 0 || s.overlap > 0
}

// An overlap is booked anyway unless they say otherwise (the diary allows
// double booking, and salons overlap on purpose); a date that can't be
// booked is skipped
// 'custom' is a time they picked for that one visit (times[date], minutes)
export const choiceFor = (d, choices, times) => {
  const c = choices && choices[d.date]
  if (c === 'move' && !d.suggestion) return d.status === 'overlap' ? 'keep' : 'skip'
  if (c === 'custom' && !(times && times[d.date] != null)) return 'custom'
  if (c) return c
  return d.status === 'overlap' ? 'keep' : 'skip'
}

// What the create call needs: dates to skip, and moves to a suggested time
// or a time they picked for that visit ([start, end] in minutes)
export const checkBody = (dates, choices, times = {}, duration = 0) => {
  const skip = []
  const moves = {}
  ;(dates || []).forEach((d) => {
    if (d.status === 'ok') return
    const c = choiceFor(d, choices, times)
    if (c === 'move' && d.suggestion) moves[d.date] = d.suggestion.bookingSlot
    else if (c === 'custom' && times[d.date] != null) moves[d.date] = [times[d.date], times[d.date] + duration]
    else if (c === 'custom' || c === 'skip') skip.push(d.date)
  })
  return { skip, moves }
}

// How many visits will be booked with these choices (a "change time" with
// no time picked yet isn't booked)
export const bookedCount = (dates, choices, times = {}) => (dates || []).filter((d) => {
  if (d.status === 'ok') return true
  const c = choiceFor(d, choices, times)
  return c === 'custom' ? times[d.date] != null : c !== 'skip'
}).length

// A time of day in minutes, as the diary says it
export const minutesSaid = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

// ─── Edit and cancel: which visits ───
const visitId = v => String(v.bookingId || v._id || v.id || '')
const visitDay = v => moment(v.date || v.bookingDate || v.start)

// The visits around this one, when the server lists them (series.visits);
// null until it does, and the words then go without numbers
export const visitsAround = (series, bookingId, now = new Date()) => {
  const all = series && Array.isArray(series.visits) ? series.visits : null
  if (!all) return null
  const live = all.filter(v => v && !/cancel/i.test(String(v.status || ''))).sort((a, b) => visitDay(a) - visitDay(b))
  const i = live.findIndex(v => visitId(v) === String(bookingId))
  if (i < 0) return null
  const today = moment(now).startOf('day')
  const following = live.slice(i)
  return {
    following,
    before: live.slice(0, i).filter(v => !visitDay(v).isBefore(today)),
    last: following[following.length - 1],
  }
}

// Cancel: the line under each choice
export const scopeDetail = (scope, around, thisDate) => {
  if (scope === 'this') return thisDate ? dateSaid(moment(thisDate).format('YYYY-MM-DD')) : ''
  if (!around) return scope === 'following' ? 'This visit and every one after it' : 'Every upcoming visit, earlier ones too'
  const n = scope === 'following' ? around.following.length : around.following.length + around.before.length
  return `${n} visit${n === 1 ? '' : 's'}, to ${dateSaid(visitDay(around.last).format('YYYY-MM-DD'))}`
}

// "All upcoming" only when there are upcoming visits before this one;
// otherwise it's the same as "this and all after it"
export const cancelScopes = (around) => (around && !around.before.length ? ['this', 'following'] : ['this', 'following', 'all'])

// Cancel: the message and the red button, following the choice
export const cancelWords = (scope, around) => {
  if (scope === 'this') return { text: 'This will cancel the booking. Your client will be notified and sent an email.', button: 'Cancel Booking' }
  const n = around ? (scope === 'following' ? around.following.length : around.following.length + around.before.length) : null
  const what = n ? `${n} visit${n === 1 ? '' : 's'}` : scope === 'all' ? 'every upcoming visit' : 'this visit and every one after it'
  return {
    text: `This cancels ${what} and stops the repeat. Your client gets one email about the cancelled visits.`,
    button: n ? `Cancel ${n} visit${n === 1 ? '' : 's'}` : 'Cancel these visits',
  }
}

// Edit: the "this and later" choice and the line under it
export const editWords = (scope, around) => {
  const n = around ? around.following.length : null
  return {
    later: n ? `This and later (${n})` : 'This and later',
    line: scope !== 'following' ? 'Only this visit changes.'
      : n && n > 1 ? `Also changes the ${n - 1} visit${n - 1 === 1 ? '' : 's'} after this one, to ${dateSaid(visitDay(around.last).format('YYYY-MM-DD'))}.`
        : 'Also changes every later visit in this repeat to match.',
    button: scope === 'following' && n ? `Save ${n} visits` : 'Save Changes',
  }
}

// ─── Ending soon ───
// A repeat whose last booked visit is close (the server lists them:
// booking-series/ending-soon). Four weeks' notice, so there's time to keep
// the client's day and time.
export const ENDING_NOTICE_DAYS = 28
export const endsSoon = (series, now = new Date()) => (series || []).filter((x) => {
  const last = x && x.lastVisit && x.lastVisit.date
  if (!last) return false
  const days = moment(last).startOf('day').diff(moment(now).startOf('day'), 'days')
  return days >= 0 && days <= ENDING_NOTICE_DAYS
})

// The panel's repeat choice for a rule ({ every } or { monthly })
export const freqFor = (rule) => {
  if (!rule) return 'none'
  if (rule.monthly) return 'm1'
  const f = FREQUENCIES.find(x => x.rule && x.rule.every === Number(rule.every))
  return f ? f.id : 'none'
}

// Extending: a new repeat from the next visit after the last one, same
// client, stylist, services and time, through Check the dates as usual
export const extendSeed = (x) => {
  const last = moment(x.lastVisit.date)
  const next = x.rule && x.rule.monthly ? monthlyDate(last, 1) : moment(last).add(7 * (Number(x.rule && x.rule.every) || 1), 'days')
  return {
    type: 'booking',
    staffId: x.staffId,
    bookingDate: next.format('YYYY-MM-DD'),
    startMinutes: x.lastVisit.startMinutes,
    client: x.client || null,
    services: x.services || [],
    repeat: { freq: freqFor(x.rule), end: 'never', endDate: '', endCount: 6 },
  }
}

// ─── Pause ───
// Skip the visits between two dates (both included) and carry on after.
// Quick picks for the last date, from the first.
export const PAUSE_PICKS = [
  { days: 14, label: '2 weeks' },
  { days: 28, label: '4 weeks' },
  { months: 3, label: '3 months' },
]
export const pauseUntil = (from, pick) => moment(from).add(pick.days || 0, 'days').add(pick.months || 0, 'months').subtract(1, 'day').format('YYYY-MM-DD')

// What's wrong with the two dates, as the server says it, or null
export const pauseProblem = (from, to) => {
  if (!from || !to) return 'Choose the first and last date.'
  if (moment(to).isBefore(moment(from), 'day')) return 'Choose a last date on or after the first.'
  if (moment(to).diff(moment(from), 'days') > 366) return 'Pause for a year at most. To stop it for good, use Stop repeating.'
  return null
}

// The booked visits between the two dates, when the server lists them
// (series.visits); null until it does
export const visitsToPause = (series, from, to) => {
  const all = series && Array.isArray(series.visits) ? series.visits : null
  if (!all) return null
  if (!from || !to) return []
  return all
    .filter(v => v && (!v.status || v.status === 'booked') && v.date >= from && v.date <= to)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

const dayMonth = d => moment(d, 'YYYY-MM-DD').format('D MMM')

// The line under the dates and the button: "Skips 3 visits, 14 Oct to
// 28 Oct. The repeat carries on after." / "Pause 3 visits"
export const pauseWords = (visits, from, to) => {
  const after = 'The repeat carries on after.'
  if (!visits) return { line: `Skips every visit from ${dayMonth(from)} to ${dayMonth(to)}. ${after}`, button: 'Pause', none: false }
  if (!visits.length) return { line: `No visits fall between ${dayMonth(from)} and ${dayMonth(to)}.`, button: 'Nothing to pause', none: true }
  const n = visits.length
  const when = n === 1 ? dateSaid(visits[0].date) : `${dayMonth(visits[0].date)} to ${dayMonth(visits[n - 1].date)}`
  return { line: `Skips ${n} visit${n === 1 ? '' : 's'}, ${when}. ${after}`, button: `Pause ${n} visit${n === 1 ? '' : 's'}`, none: false }
}

// ─── Change how often ───
// The repeat as it is now, for the controls: the server's rule when it
// sends one, else read from its words ("every 6 weeks until 12 Mar 2027",
// "every week for 8 visits", "every month on the 2nd Tuesday with no end
// date"). A count is counted from this visit on, so it starts at the
// visits left.
export const repeatFromSeries = (series, around) => {
  const s = series || {}
  const said = String(s.said || '').toLowerCase()
  let freq = s.rule ? freqFor(s.rule) : 'none'
  if (freq === 'none') {
    const weeks = said.match(/every (\d+) weeks/)
    if (/every month/.test(said)) freq = 'm1'
    else if (weeks) freq = freqFor({ every: Number(weeks[1]) })
    else if (/every week/.test(said)) freq = 'w1'
  }
  if (freq === 'none') freq = 'w1'
  const left = around && around.following ? around.following.length : null
  const u = s.until || {}
  const date = u.date || ((said.match(/until (\d{1,2} \w{3} \d{4})/) || [])[1] && moment(said.match(/until (\d{1,2} \w{3} \d{4})/)[1], 'D MMM YYYY').format('YYYY-MM-DD'))
  if (date) return { freq, end: 'date', endDate: date, endCount: 6 }
  if (u.count || /for \d+ visits/.test(said)) return { freq, end: 'count', endDate: '', endCount: Math.max(2, left || Number(u.count) || Number((said.match(/for (\d+) visits/) || [])[1]) || 6) }
  return { freq, end: 'never', endDate: '', endCount: 6 }
}

// Nothing changed yet (the button stays off)
export const sameRepeat = (a, b) => !!a && !!b && a.freq === b.freq && a.end === b.end
  && (a.end !== 'date' || a.endDate === b.endDate) && (a.end !== 'count' || Number(a.endCount) === Number(b.endCount))

// The check runs on the stylist's diary as it is, so this visit and the
// repeat's own later visits (which are rebooked) show as overlaps. They
// aren't clashes: this visit stays, and the others move. Anyone else
// still overlapping is named, as the server does.
export const withoutOwnVisits = (dates, ownIds, firstDate) => {
  const own = new Set((ownIds || []).map(String))
  return (dates || []).map((d) => {
    if (d.date === firstDate) return { date: d.date, status: 'ok' }
    if (d.status !== 'overlap' || !Array.isArray(d.overlapsWith) || !d.overlapsWith.length) return d
    const others = d.overlapsWith.filter(o => !own.has(String(o.bookingId)))
    if (others.length === d.overlapsWith.length) return d
    if (!others.length) return { date: d.date, status: 'ok' }
    const first = others[0]
    const reason = `overlaps ${first.name}, ${minutesSaid(first.start)} to ${minutesSaid(first.end)}${others.length > 1 ? `, and ${others.length - 1} more` : ''}`
    return { ...d, reason, overlaps: others.length, overlapsWith: others }
  })
}

// The repeat's own visits still on the books, and this one
export const ownVisitIds = (series, bookingId) => {
  const visits = series && Array.isArray(series.visits) ? series.visits : []
  return [String(bookingId), ...visits.filter(v => v && !/cancel/i.test(String(v.status || ''))).map(v => String(v.bookingId))]
}

// What change-rule gets: the new rule and end, from this visit, with the
// dates to skip and the times picked, built exactly as a new repeat's are.
// This visit stays as it is, so it's never skipped or moved.
export const changeRuleBody = ({ bookingId, repeat, firstDate, dates, choices, times, duration, now }) => {
  const later = (dates || []).filter(d => d.date !== firstDate)
  const { skip, moves } = checkBody(later, choices || {}, times || {}, duration || 0)
  return { bookingId, rule: ruleFor(repeat.freq), until: untilToSend(repeat, firstDate, now), skip, moves }
}
